import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  User,
  Phone,
  Mail,
  Loader2,
  CheckCircle,
  XCircle,
  Play,
  Square,
  Edit,
  Trash2,
  UserPlus,
  Sparkles,
  AlertTriangle,
  ClipboardList,
  Camera,
  Image as ImageIcon,
  Upload,
  Package,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  Share2,
  Download,
  Printer,
} from 'lucide-react';
import { formatDate, formatTime, formatDuration, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import ImageLightbox from '../components/ImageLightbox';

const BookingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { api, user, isManager, isDirector } = useAuth();
  
  // Check if coming from job completion
  const justCompleted = new URLSearchParams(location.search).get('completed') === 'true';
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [staff, setStaff] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [conflicts, setConflicts] = useState([]);
  const [overrideReason, setOverrideReason] = useState('');
  
  // Checklist and Photos state
  const [checklist, setChecklist] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoType, setPhotoType] = useState('after');
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [completionStatus, setCompletionStatus] = useState(null);
  const fileInputRef = useRef(null);
  
  // Checklist item photos state
  const [uploadingItemPhoto, setUploadingItemPhoto] = useState(null); // item.id being uploaded
  const [viewingItemPhotos, setViewingItemPhotos] = useState(null); // item for photo lightbox
  const checklistPhotoInputRef = useRef(null);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxSlides, setLightboxSlides] = useState([]);
  
  // Not Done reason dialog state
  const [showNotDoneDialog, setShowNotDoneDialog] = useState(false);
  const [notDoneItem, setNotDoneItem] = useState(null);
  const [notDoneReason, setNotDoneReason] = useState('');

  useEffect(() => {
    fetchBooking();
    if (isManager) {
      fetchStaff();
    }
  }, [id]);

  const fetchBooking = async () => {
    try {
      const response = await api().get(`/bookings/${id}`);
      setBooking(response.data);
      // Fetch checklist if booking has a package
      if (response.data.package_id || response.data.checklist_snapshot?.length > 0) {
        fetchChecklist();
      }
      // Always fetch photos and completion status
      fetchPhotos();
      fetchCompletionStatus();
    } catch (error) {
      console.error('Failed to fetch booking:', error);
      toast.error('Failed to load booking');
      navigate('/bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklist = async () => {
    try {
      const response = await api().get(`/bookings/${id}/checklist`);
      setChecklist(response.data.checklist || []);
    } catch (error) {
      console.error('Failed to fetch checklist:', error);
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await api().get(`/bookings/${id}/photos`);
      setPhotos(response.data || []);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    }
  };

  const fetchCompletionStatus = async () => {
    try {
      const response = await api().get(`/bookings/${id}/completion-status`);
      setCompletionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch completion status:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await api().get('/users/staff/available');
      setStaff(response.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const handleConfirm = async () => {
    setActionLoading(true);
    try {
      await api().post(`/bookings/${id}/confirm`);
      toast.success('Booking confirmed! Email sent to client.');
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm booking');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = async (forceOverride = false) => {
    if (!selectedStaff) {
      toast.error('Please select a staff member');
      return;
    }
    setActionLoading(true);
    try {
      const params = new URLSearchParams({ staff_id: selectedStaff });
      if (forceOverride) {
        params.append('override_conflict', 'true');
        if (overrideReason) {
          params.append('override_reason', overrideReason);
        }
      }
      
      const response = await api().post(`/bookings/${id}/assign?${params.toString()}`);
      
      // Check if there's a conflict warning
      if (response.data.status === 'conflict_warning') {
        setConflicts(response.data.conflicts);
        setShowAssignDialog(false);
        setShowConflictDialog(true);
        setActionLoading(false);
        return;
      }
      
      toast.success('Staff assigned successfully');
      setShowAssignDialog(false);
      setShowConflictDialog(false);
      setConflicts([]);
      setOverrideReason('');
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign staff');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAccept = async () => {
    setActionLoading(true);
    try {
      await api().post(`/bookings/${id}/accept`);
      toast.success('Job accepted!');
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept job');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (declineReason.length < 5) {
      toast.error('Please provide a reason for declining');
      return;
    }
    setActionLoading(true);
    try {
      await api().post(`/bookings/${id}/decline?reason=${encodeURIComponent(declineReason)}`);
      toast.success('Job declined');
      setShowDeclineDialog(false);
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline job');
    } finally {
      setActionLoading(false);
    }
  };

  const handleArrive = async () => {
    setActionLoading(true);
    try {
      await api().post(`/bookings/${id}/arrive`);
      toast.success('Arrived! Have a great clean!');
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record arrival');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDepart = async () => {
    setActionLoading(true);
    try {
      await api().post(`/bookings/${id}/depart`);
      toast.success('Job completed! Great work!');
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record departure');
    } finally {
      setActionLoading(false);
    }
  };

  // Checklist handlers
  const handleChecklistItemToggle = async (itemId, newStatus) => {
    // If marking as "done", proceed directly
    if (newStatus === 'done' || newStatus === true) {
      try {
        await api().patch(`/bookings/${id}/checklist/${itemId}`, { status: 'done' });
        fetchChecklist();
        toast.success('Item marked as done');
      } catch (error) {
        toast.error('Failed to update checklist');
      }
      fetchCompletionStatus();
    } 
    // If marking as "not_done", show dialog to get reason
    else if (newStatus === 'not_done') {
      const item = checklist.find(i => i.id === itemId);
      setNotDoneItem(item);
      setNotDoneReason('');
      setShowNotDoneDialog(true);
    }
    // If resetting to pending
    else if (newStatus === 'pending' || newStatus === false) {
      try {
        await api().patch(`/bookings/${id}/checklist/${itemId}`, { status: 'pending' });
        fetchChecklist();
        toast.success('Item reset to pending');
      } catch (error) {
        toast.error('Failed to update checklist');
      }
      fetchCompletionStatus();
    }
  };
  
  // Submit not done reason
  const handleNotDoneSubmit = async () => {
    if (!notDoneItem || !notDoneReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    
    try {
      await api().patch(`/bookings/${id}/checklist/${notDoneItem.id}`, { 
        status: 'not_done',
        not_done_reason: notDoneReason.trim()
      });
      fetchChecklist();
      fetchCompletionStatus();
      setShowNotDoneDialog(false);
      setNotDoneItem(null);
      setNotDoneReason('');
      toast.success('Item marked as Not Done with reason');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update checklist');
    }
  };

  const getChecklistGroups = () => {
    const groups = {};
    checklist.forEach(item => {
      if (!groups[item.group]) groups[item.group] = [];
      groups[item.group].push(item);
    });
    return groups;
  };

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const checklistProgress = checklist.length > 0 
    ? Math.round((checklist.filter(i => i.completed || i.status === 'done').length / checklist.length) * 100)
    : 0;

  // Photo handlers
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('photo_type', photoType);

      await api().post(`/bookings/${id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded');
      fetchPhotos();
      fetchCompletionStatus();
      setShowPhotoUpload(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await api().delete(`/bookings/${id}/photos/${photoId}`);
      toast.success('Photo deleted');
      fetchPhotos();
      fetchCompletionStatus();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  // Checklist Item Photo Handlers
  const handleChecklistItemPhotoUpload = async (itemId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploadingItemPhoto(itemId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api().post(`/bookings/${id}/checklist/${itemId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Photo uploaded');
      fetchChecklist();
      fetchCompletionStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingItemPhoto(null);
      // Reset file input
      if (checklistPhotoInputRef.current) {
        checklistPhotoInputRef.current.value = '';
      }
    }
  };

  const handleDeleteChecklistItemPhoto = async (itemId, photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    
    try {
      await api().delete(`/bookings/${id}/checklist/${itemId}/photos/${photoId}`);
      toast.success('Photo deleted');
      fetchChecklist();
      fetchCompletionStatus();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const hasAfterPhoto = photos.some(p => p.photo_type === 'after');
  const canCompleteJob = completionStatus?.can_complete || false;

  // Open lightbox with booking photos
  const openLightbox = (photoList, index = 0) => {
    const slides = photoList.map((photo) => ({
      src: `${process.env.REACT_APP_BACKEND_URL}${photo.url}`,
      alt: photo.photo_type || 'Photo',
    }));
    setLightboxSlides(slides);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Open lightbox for checklist item photos
  const openItemLightbox = (item, index = 0) => {
    const slides = item.photos.map((photo) => ({
      src: `${process.env.REACT_APP_BACKEND_URL}/api/bookings/${id}/checklist/${item.id}/photos/${photo.filename}/file`,
      alt: item.task || 'Item photo',
    }));
    setLightboxSlides(slides);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await api().post(`/bookings/${id}/cancel?reason=${encodeURIComponent(cancelReason)}`);
      toast.success('Booking cancelled');
      setShowCancelDialog(false);
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  const isAssignedToMe = booking.assigned_staff_id === user?.id;
  const canAccept = booking.status === 'assigned' && isAssignedToMe;
  const canArrive = booking.status === 'accepted' && isAssignedToMe && !booking.checkin_time;
  const canDepart = booking.status === 'in_progress' && isAssignedToMe;

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/bookings')}
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold text-zinc-900">Booking Details</h1>
            <Badge className={getStatusColor(booking.status)}>
              {getStatusLabel(booking.status)}
            </Badge>
          </div>
          <p className="text-zinc-500 text-sm mt-1">
            Created {formatDate(booking.created_at)}
          </p>
        </div>
        {/* Edit button for managers/directors */}
        {isManager && booking.status !== 'completed' && booking.status !== 'cancelled' && (
          <Button 
            variant="outline" 
            onClick={() => navigate(`/bookings/${id}/edit`)}
            data-testid="edit-booking-btn"
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </Button>
        )}
      </div>

      {/* JOB COMPLETION SUMMARY - Shows for completed bookings */}
      {booking.status === 'completed' && (
        <Card className={`border-emerald-200 ${justCompleted ? 'bg-emerald-100 ring-2 ring-emerald-400' : 'bg-emerald-50/50'}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl flex items-center gap-2 text-emerald-800">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
                {justCompleted ? 'Job Complete! 🎉' : 'Job Completion Summary'}
              </CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="bg-white"
                  onClick={() => window.print()}
                >
                  <Printer className="h-4 w-4 mr-1" />
                  Print
                </Button>
                <Button 
                  variant="default" 
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    const summaryText = `Job Complete - ${booking.client_name}\n` +
                      `Address: ${booking.service_address}\n` +
                      `Date: ${formatDate(booking.scheduled_date)}\n` +
                      `Staff: ${booking.assigned_staff_name || 'N/A'}\n` +
                      `Time on Site: ${booking.total_time_on_site_minutes || 0} mins\n` +
                      `Checklist: ${checklist.filter(i => i.completed || i.status === 'done').length}/${checklist.length} completed\n` +
                      `Photos: ${photos.length} uploaded\n\n` +
                      `View full details: ${window.location.href.split('?')[0]}`;
                    
                    if (navigator.share) {
                      navigator.share({
                        title: `Job Complete - ${booking.client_name}`,
                        text: summaryText,
                        url: window.location.href.split('?')[0]
                      });
                    } else {
                      navigator.clipboard.writeText(summaryText);
                      toast.success('Summary copied to clipboard!');
                    }
                  }}
                >
                  <Share2 className="h-4 w-4 mr-1" />
                  Share Summary
                </Button>
              </div>
            </div>
            {justCompleted && (
              <p className="text-emerald-700 mt-2">
                Great work! Here's the job summary to share with your team.
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Time Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-3 border border-emerald-100">
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Arrived</p>
                <p className="text-lg font-semibold text-zinc-900">
                  {booking.checkin_time ? formatTime(booking.checkin_time) : '-'}
                </p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-emerald-100">
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Departed</p>
                <p className="text-lg font-semibold text-zinc-900">
                  {booking.checkout_time ? formatTime(booking.checkout_time) : '-'}}
                </p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-emerald-100">
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Time on Site</p>
                <p className="text-lg font-semibold text-zinc-900">
                  {booking.total_time_on_site_minutes ? `${booking.total_time_on_site_minutes} mins` : '-'}
                </p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-emerald-100">
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Completed By</p>
                <p className="text-lg font-semibold text-zinc-900 truncate">
                  {booking.assigned_staff_name || '-'}
                </p>
              </div>
            </div>

            {/* Checklist Summary */}
            {checklist.length > 0 && (
              <div className="bg-white rounded-lg p-4 border border-emerald-100">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-zinc-900 flex items-center gap-2">
                    <ClipboardList className="h-5 w-5 text-emerald-600" />
                    Checklist Results
                  </h3>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="flex items-center gap-1 text-emerald-600">
                      <CheckCircle className="h-4 w-4" />
                      {checklist.filter(i => i.completed || i.status === 'done').length} Done
                    </span>
                    <span className="flex items-center gap-1 text-amber-600">
                      <XCircle className="h-4 w-4" />
                      {checklist.filter(i => i.status === 'not_done').length} Skipped
                    </span>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-zinc-200 rounded-full h-3 mb-4">
                  <div 
                    className="bg-emerald-500 h-3 rounded-full transition-all"
                    style={{ width: `${checklistProgress}%` }}
                  />
                </div>

                {/* Items marked as Not Done with reasons */}
                {checklist.filter(i => i.status === 'not_done').length > 0 && (
                  <div className="space-y-2 mt-3 pt-3 border-t">
                    <p className="text-sm font-medium text-amber-700">Items Skipped:</p>
                    {checklist.filter(i => i.status === 'not_done').map(item => (
                      <div key={item.id} className="bg-amber-50 rounded p-2 text-sm">
                        <span className="font-medium text-amber-800">{item.task}</span>
                        {item.not_done_reason && (
                          <p className="text-amber-700 mt-1">Reason: {item.not_done_reason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Photos Summary */}
            {photos.length > 0 && (
              <div className="bg-white rounded-lg p-4 border border-emerald-100">
                <h3 className="font-medium text-zinc-900 flex items-center gap-2 mb-4">
                  <Camera className="h-5 w-5 text-emerald-600" />
                  Job Photos ({photos.length})
                </h3>
                
                {/* Before/After Side by Side */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Before Photos */}
                  <div>
                    <p className="text-sm font-medium text-blue-700 mb-2">Before</p>
                    <div className="grid grid-cols-2 gap-2">
                      {photos.filter(p => p.photo_type === 'before').slice(0, 4).map((photo, idx) => (
                        <div 
                          key={photo.id}
                          className="aspect-square rounded-lg overflow-hidden border cursor-pointer hover:opacity-90"
                          onClick={() => openLightbox(photos.filter(p => p.photo_type === 'before'), idx)}
                        >
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                            alt="Before"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                      {photos.filter(p => p.photo_type === 'before').length === 0 && (
                        <p className="text-sm text-zinc-400 col-span-2">No before photos</p>
                      )}
                    </div>
                  </div>
                  
                  {/* After Photos */}
                  <div>
                    <p className="text-sm font-medium text-emerald-700 mb-2">After</p>
                    <div className="grid grid-cols-2 gap-2">
                      {photos.filter(p => p.photo_type === 'after').slice(0, 4).map((photo, idx) => (
                        <div 
                          key={photo.id}
                          className="aspect-square rounded-lg overflow-hidden border cursor-pointer hover:opacity-90"
                          onClick={() => openLightbox(photos.filter(p => p.photo_type === 'after'), idx)}
                        >
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                            alt="After"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                      {photos.filter(p => p.photo_type === 'after').length === 0 && (
                        <p className="text-sm text-zinc-400 col-span-2">No after photos</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Issue Photos if any */}
                {photos.filter(p => p.photo_type === 'issue').length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium text-red-700 mb-2">Issues Reported</p>
                    <div className="grid grid-cols-4 gap-2">
                      {photos.filter(p => p.photo_type === 'issue').map((photo, idx) => (
                        <div 
                          key={photo.id}
                          className="aspect-square rounded-lg overflow-hidden border-2 border-red-300 cursor-pointer hover:opacity-90"
                          onClick={() => openLightbox(photos.filter(p => p.photo_type === 'issue'), idx)}
                        >
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                            alt="Issue"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Staff Notes */}
            {booking.staff_notes && (
              <div className="bg-white rounded-lg p-4 border border-emerald-100">
                <h3 className="font-medium text-zinc-900 mb-2">Staff Notes</h3>
                <p className="text-zinc-700 whitespace-pre-wrap">{booking.staff_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Schedule Card */}
          <Card className="border-zinc-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-zinc-500" />
                Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-zinc-500">Date</p>
                  <p className="font-medium text-zinc-900">{formatDate(booking.scheduled_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Time Window</p>
                  <p className="font-medium text-zinc-900">
                    {booking.time_window_start || 'TBC'} - {booking.time_window_end || 'TBC'}
                  </p>
                </div>
                {booking.estimated_duration_minutes && (
                  <div>
                    <p className="text-sm text-zinc-500">Estimated Duration</p>
                    <p className="font-medium text-zinc-900">
                      {formatDuration(booking.estimated_duration_minutes)}
                    </p>
                  </div>
                )}
                {booking.price && (
                  <div>
                    <p className="text-sm text-zinc-500">Price</p>
                    <p className="font-medium text-zinc-900">${booking.price.toFixed(2)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Location Card */}
          <Card className="border-zinc-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-zinc-500" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-zinc-500">Service Address</p>
                <p className="font-medium text-zinc-900">{booking.service_address}</p>
              </div>
              {booking.access_notes && (
                <div>
                  <p className="text-sm text-zinc-500">Access Instructions</p>
                  <p className="text-zinc-700">{booking.access_notes}</p>
                </div>
              )}
              {booking.job_details && (
                <div>
                  <p className="text-sm text-zinc-500">Job Details</p>
                  <p className="text-zinc-700">{booking.job_details}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timing Card (if arrived/departed) */}
          {(booking.checkin_time || booking.checkout_time) && (
            <Card className="border-zinc-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-zinc-500" />
                  Timing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {booking.checkin_time && (
                    <div>
                      <p className="text-sm text-zinc-500">Arrived</p>
                      <p className="font-medium text-emerald-600">{formatTime(booking.checkin_time)}</p>
                    </div>
                  )}
                  {booking.checkout_time && (
                    <div>
                      <p className="text-sm text-zinc-500">Departed</p>
                      <p className="font-medium text-emerald-600">{formatTime(booking.checkout_time)}</p>
                    </div>
                  )}
                  {booking.total_time_on_site_minutes && (
                    <div>
                      <p className="text-sm text-zinc-500">Total Time</p>
                      <p className="font-medium text-zinc-900">
                        {formatDuration(booking.total_time_on_site_minutes)}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Service Package Checklist Card */}
          {checklist.length > 0 && (
            <Card className="border-zinc-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-zinc-500" />
                  Checklist
                  {booking.package_name_snapshot && (
                    <Badge className="bg-blue-50 text-blue-700 ml-2">{booking.package_name_snapshot}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-600">Progress</span>
                    <span className="font-medium text-zinc-900">{checklistProgress}%</span>
                  </div>
                  <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 transition-all duration-300"
                      style={{ width: `${checklistProgress}%` }}
                    />
                  </div>
                </div>

                {/* Grouped Checklist */}
                <div className="space-y-2">
                  {Object.entries(getChecklistGroups()).map(([group, items]) => (
                    <div key={group} className="border rounded-lg overflow-hidden">
                      <button
                        className="w-full flex items-center justify-between p-3 hover:bg-zinc-50 text-left"
                        onClick={() => toggleGroup(group)}
                      >
                        <span className="font-medium text-zinc-700">{group}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-zinc-500">
                            {items.filter(i => i.completed).length}/{items.length}
                          </span>
                          {expandedGroups[group] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                      </button>
                      {expandedGroups[group] && (
                        <div className="bg-zinc-50 border-t p-2 space-y-2">
                          {items.map((item) => {
                            const itemStatus = item.status || (item.completed ? 'done' : 'pending');
                            return (
                            <div 
                              key={item.id} 
                              className={`rounded border ${
                                itemStatus === 'done' ? 'bg-emerald-50 border-emerald-200' : 
                                itemStatus === 'not_done' ? 'bg-amber-50 border-amber-200' : 
                                'bg-white border-zinc-200'
                              }`}
                            >
                              {/* Item Row */}
                              <div className="flex items-center gap-3 p-2">
                                {/* Status Indicator */}
                                <div className="flex-shrink-0">
                                  {itemStatus === 'done' && (
                                    <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                                      <CheckCircle className="h-4 w-4" />
                                    </div>
                                  )}
                                  {itemStatus === 'not_done' && (
                                    <div className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center">
                                      <XCircle className="h-4 w-4" />
                                    </div>
                                  )}
                                  {itemStatus === 'pending' && (
                                    <div className="w-6 h-6 rounded-full border-2 border-zinc-300" />
                                  )}
                                </div>
                                
                                {/* Task Name */}
                                <span className={`flex-1 text-sm ${
                                  itemStatus === 'done' ? 'text-zinc-500 line-through' : 
                                  itemStatus === 'not_done' ? 'text-amber-700' : 
                                  'text-zinc-700'
                                }`}>
                                  {item.task}
                                </span>
                                
                                {/* Status Actions for Staff */}
                                {isAssignedToMe && booking.status !== 'completed' && (
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleChecklistItemToggle(item.id, 'done')}
                                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                        itemStatus === 'done' 
                                          ? 'bg-emerald-500 text-white' 
                                          : 'bg-zinc-100 text-zinc-600 hover:bg-emerald-100 hover:text-emerald-700'
                                      }`}
                                      data-testid={`done-btn-${item.id}`}
                                    >
                                      Done
                                    </button>
                                    <button
                                      onClick={() => handleChecklistItemToggle(item.id, 'not_done')}
                                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                        itemStatus === 'not_done' 
                                          ? 'bg-amber-500 text-white' 
                                          : 'bg-zinc-100 text-zinc-600 hover:bg-amber-100 hover:text-amber-700'
                                      }`}
                                      data-testid={`not-done-btn-${item.id}`}
                                    >
                                      Not Done
                                    </button>
                                  </div>
                                )}
                                
                                <div className="flex items-center gap-2">
                                  {item.required && itemStatus === 'pending' && (
                                    <Badge className="bg-amber-50 text-amber-700 text-xs">Required</Badge>
                                  )}
                                  {item.photo_required && (
                                    <Badge className={`text-xs ${(item.photos?.length || 0) > 0 ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>
                                      📷 {item.photos?.length || 0}
                                    </Badge>
                                  )}
                                  {/* Photo Upload Button */}
                                  {isAssignedToMe && booking.status !== 'completed' && (
                                    <label className="cursor-pointer p-1 rounded hover:bg-zinc-100 transition-colors">
                                      {uploadingItemPhoto === item.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                                      ) : (
                                        <Camera className="h-4 w-4 text-zinc-400 hover:text-blue-500" />
                                      )}
                                      <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        className="hidden"
                                        onChange={(e) => handleChecklistItemPhotoUpload(item.id, e)}
                                        disabled={uploadingItemPhoto === item.id}
                                      />
                                    </label>
                                  )}
                                </div>
                              </div>
                              
                              {/* Not Done Reason Display */}
                              {itemStatus === 'not_done' && item.not_done_reason && (
                                <div className="px-3 pb-2 border-t border-amber-200 bg-amber-50/50">
                                  <p className="text-xs text-amber-700 mt-2">
                                    <span className="font-medium">Reason:</span> {item.not_done_reason}
                                  </p>
                                </div>
                              )}
                              
                              {/* Item Photos (thumbnails) */}
                              {(item.photos?.length || 0) > 0 && (
                                <div className="px-2 pb-2 flex flex-wrap gap-2">
                                  {item.photos.map((photo, idx) => (
                                    <div key={photo.id} className="relative group">
                                      <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${id}/checklist/${item.id}/photos/${photo.thumbnail}/file`}
                                        alt="Checklist item photo"
                                        className="w-16 h-16 object-cover rounded cursor-pointer border border-zinc-200 hover:opacity-80 transition-opacity"
                                        loading="lazy"
                                        onClick={() => openItemLightbox(item, idx)}
                                        data-testid={`checklist-photo-${item.id}-${idx}`}
                                      />
                                      {isAssignedToMe && booking.status !== 'completed' && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleDeleteChecklistItemPhoto(item.id, photo.id); }}
                                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                          ×
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )})}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Photos Card */}
          {(isAssignedToMe || isManager) && (booking.status === 'in_progress' || booking.status === 'accepted' || booking.status === 'completed') && (
            <Card className="border-zinc-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Camera className="h-5 w-5 text-zinc-500" />
                  Photos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Photo Upload Section */}
                {booking.status !== 'completed' && isAssignedToMe && (
                  <div className="mb-4">
                    {!showPhotoUpload ? (
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={() => setShowPhotoUpload(true)}
                        data-testid="add-photo-btn"
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Upload Photo
                      </Button>
                    ) : (
                      <div className="space-y-3 p-3 bg-zinc-50 rounded-lg">
                        <div>
                          <Label>Photo Type</Label>
                          <Select value={photoType} onValueChange={setPhotoType}>
                            <SelectTrigger className="mt-1" data-testid="photo-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="before">Before</SelectItem>
                              <SelectItem value="during">During</SelectItem>
                              <SelectItem value="after">After</SelectItem>
                              <SelectItem value="issue">Issue</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="w-full text-sm"
                          data-testid="photo-file-input"
                        />
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setShowPhotoUpload(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                        {uploadingPhoto && (
                          <div className="flex items-center gap-2 text-sm text-zinc-500">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Uploading...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* After Photo Warning */}
                {booking.status === 'in_progress' && isAssignedToMe && !hasAfterPhoto && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800">After photo required</p>
                      <p className="text-amber-700">Please upload at least one "After" photo before completing this job.</p>
                    </div>
                  </div>
                )}

                {/* Photos Grid */}
                {photos.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {photos.map((photo, idx) => (
                      <div 
                        key={photo.id} 
                        className="relative group rounded-lg overflow-hidden border cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => openLightbox(photos, idx)}
                        data-testid={`booking-photo-${idx}`}
                      >
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                          alt={`${photo.photo_type} photo`}
                          className="w-full h-40 object-cover"
                        />
                        <div className="absolute top-2 left-2">
                          <Badge className={`text-xs ${
                            photo.photo_type === 'before' ? 'bg-blue-500 text-white' :
                            photo.photo_type === 'after' ? 'bg-emerald-500 text-white' :
                            photo.photo_type === 'issue' ? 'bg-red-500 text-white' :
                            'bg-zinc-500 text-white'
                          }`}>
                            {photo.photo_type}
                          </Badge>
                        </div>
                        {(photo.uploaded_by === user?.id || isManager) && booking.status !== 'completed' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Delete photo"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                        {photo.uploaded_by_name && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                            <p className="text-xs text-white truncate">{photo.uploaded_by_name}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-zinc-500">
                    <ImageIcon className="h-10 w-10 mx-auto mb-2 text-zinc-300" />
                    <p className="text-sm">No photos uploaded yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Client & Actions */}
        <div className="space-y-6">
          {/* Client Card */}
          {booking.client && (
            <Card className="border-zinc-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5 text-zinc-500" />
                  Client
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link
                  to={`/clients/${booking.client_id}`}
                  className="font-medium text-zinc-900 hover:text-zinc-600"
                >
                  {booking.client.name}
                </Link>
                {booking.client.phone && (
                  <a
                    href={`tel:${booking.client.phone}`}
                    className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900"
                  >
                    <Phone className="h-4 w-4" />
                    {booking.client.phone}
                  </a>
                )}
                {booking.client.email && (
                  <a
                    href={`mailto:${booking.client.email}`}
                    className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900"
                  >
                    <Mail className="h-4 w-4" />
                    {booking.client.email}
                  </a>
                )}
                {booking.client.pets && (
                  <div>
                    <p className="text-sm text-zinc-500">Pets</p>
                    <p className="text-sm text-zinc-700">{booking.client.pets}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Assigned Staff Card */}
          <Card className="border-zinc-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-zinc-500" />
                Assigned Staff
              </CardTitle>
            </CardHeader>
            <CardContent>
              {booking.assigned_staff ? (
                <div className="space-y-2">
                  <p className="font-medium text-zinc-900">{booking.assigned_staff.full_name}</p>
                  {booking.assigned_staff.phone && (
                    <a
                      href={`tel:${booking.assigned_staff.phone}`}
                      className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900"
                    >
                      <Phone className="h-4 w-4" />
                      {booking.assigned_staff.phone}
                    </a>
                  )}
                </div>
              ) : (
                <p className="text-zinc-500">No staff assigned yet</p>
              )}
              {isManager && booking.status === 'confirmed' && (
                <Button
                  className="w-full mt-4"
                  onClick={() => setShowAssignDialog(true)}
                  data-testid="assign-staff-btn"
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Assign Staff
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Actions Card */}
          <Card className="border-zinc-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Manager Actions */}
              {isManager && (
                <>
                  {booking.status === 'draft' && (
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleConfirm}
                      disabled={actionLoading}
                      data-testid="confirm-booking-btn"
                    >
                      {actionLoading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle className="mr-2 h-4 w-4" />
                      )}
                      Confirm Booking
                    </Button>
                  )}
                  {['draft', 'confirmed', 'assigned', 'accepted'].includes(booking.status) && (
                    <Button
                      variant="outline"
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setShowCancelDialog(true)}
                      data-testid="cancel-booking-btn"
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Cancel Booking
                    </Button>
                  )}
                </>
              )}

              {/* Staff Actions */}
              {canAccept && (
                <>
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleAccept}
                    disabled={actionLoading}
                    data-testid="accept-job-btn"
                  >
                    {actionLoading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle className="mr-2 h-4 w-4" />
                    )}
                    Accept Job
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full text-red-600 hover:text-red-700"
                    onClick={() => setShowDeclineDialog(true)}
                    data-testid="decline-job-btn"
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Decline Job
                  </Button>
                </>
              )}

              {canArrive && (
                <Button
                  className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleArrive}
                  disabled={actionLoading}
                  data-testid="arrive-btn"
                >
                  {actionLoading ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <Play className="mr-2 h-5 w-5" />
                  )}
                  Arrive - I'm Here
                </Button>
              )}

              {/* Completion Status Panel - show when in progress */}
              {booking.status === 'in_progress' && completionStatus && (
                <div className="bg-zinc-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-zinc-900 text-sm">Completion Status</h4>
                  
                  {/* Checklist Status */}
                  {completionStatus.checklist.total_items > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        {completionStatus.checklist.is_complete ? (
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                        <span className="text-zinc-600">Checklist Complete</span>
                      </div>
                      <span className={completionStatus.checklist.is_complete ? 'text-emerald-600 font-medium' : 'text-zinc-500'}>
                        {completionStatus.checklist.completed_required}/{completionStatus.checklist.required_items} required
                      </span>
                    </div>
                  )}

                  {/* After Photo Status */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {completionStatus.photos.is_complete ? (
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                      <span className="text-zinc-600">After Photo Uploaded</span>
                    </div>
                    <span className={completionStatus.photos.is_complete ? 'text-emerald-600 font-medium' : 'text-zinc-500'}>
                      {completionStatus.photos.after_count} photo(s)
                    </span>
                  </div>

                  {/* Item Photos Status (if required) */}
                  {completionStatus.item_photos?.require_item_photos && completionStatus.item_photos.photo_required_items > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        {completionStatus.item_photos.is_complete ? (
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                        <span className="text-zinc-600">Checklist Item Photos</span>
                      </div>
                      <span className={completionStatus.item_photos.is_complete ? 'text-emerald-600 font-medium' : 'text-zinc-500'}>
                        {completionStatus.item_photos.items_with_photos}/{completionStatus.item_photos.photo_required_items} items
                      </span>
                    </div>
                  )}

                  {/* Arrive/Depart Status */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {completionStatus.timestamps.has_arrival ? (
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                      <span className="text-zinc-600">Arrival Recorded</span>
                    </div>
                    <span className={completionStatus.timestamps.has_arrival ? 'text-emerald-600 font-medium' : 'text-zinc-500'}>
                      {completionStatus.timestamps.has_arrival ? '✓' : 'Pending'}
                    </span>
                  </div>

                  {/* Warning if not ready */}
                  {!canCompleteJob && (
                    <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                      <AlertTriangle className="h-3 w-3 inline mr-1" />
                      Please complete required checklist items, upload required photos, and record arrival.
                    </div>
                  )}
                </div>
              )}

              {canDepart && (
                <Button
                  className={`w-full h-14 text-lg ${canCompleteJob ? 'bg-amber-600 hover:bg-amber-700' : 'bg-zinc-400 cursor-not-allowed'}`}
                  onClick={handleDepart}
                  disabled={actionLoading || !canCompleteJob}
                  data-testid="depart-btn"
                  title={!canCompleteJob ? 'Complete required items first' : 'Mark job as complete'}
                >
                  {actionLoading ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <Square className="mr-2 h-5 w-5" />
                  )}
                  Depart - Job Done
                </Button>
              )}

              {booking.status === 'completed' && (
                <div className="flex items-center justify-center gap-2 py-4 text-emerald-600">
                  <Sparkles className="h-5 w-5" />
                  <span className="font-medium">Job Completed!</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Assign Staff Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Staff Member</DialogTitle>
            <DialogDescription>
              Choose a staff member to assign to this job.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="staff">Staff Member</Label>
            <Select value={selectedStaff} onValueChange={setSelectedStaff}>
              <SelectTrigger data-testid="staff-select">
                <SelectValue placeholder="Select staff member" />
              </SelectTrigger>
              <SelectContent>
                {staff.map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.full_name} ({member.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={actionLoading} data-testid="confirm-assign-btn">
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Job</DialogTitle>
            <DialogDescription>
              Please provide a reason for declining this job.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="decline-reason">Reason</Label>
            <Textarea
              id="decline-reason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="e.g., Schedule conflict, too far away..."
              className="mt-2"
              data-testid="decline-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeclineDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDecline}
              disabled={actionLoading || declineReason.length < 5}
              data-testid="confirm-decline-btn"
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this booking? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="cancel-reason">Reason (optional)</Label>
            <Textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g., Client request, rescheduling..."
              className="mt-2"
              data-testid="cancel-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Keep Booking
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={actionLoading}
              data-testid="confirm-cancel-btn"
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Cancel Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conflict Warning Dialog */}
      <Dialog open={showConflictDialog} onOpenChange={setShowConflictDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Scheduling Conflict Detected
            </DialogTitle>
            <DialogDescription>
              This staff member already has {conflicts.length} booking(s) that may overlap. You can still assign them if needed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3 max-h-60 overflow-y-auto">
            <p className="text-sm font-medium text-zinc-700">Conflicting bookings:</p>
            {conflicts.map((conflict, index) => (
              <div key={conflict.booking_id || index} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-zinc-900">{conflict.client_name}</p>
                    <p className="text-sm text-zinc-600">{conflict.service_address}</p>
                    <p className="text-sm text-amber-700 mt-1">
                      <Clock className="inline h-3 w-3 mr-1" />
                      {conflict.time_window_start || 'TBC'} - {conflict.time_window_end || 'TBC'}
                    </p>
                  </div>
                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                    {conflict.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="py-2">
            <Label htmlFor="override-reason">Reason for override (optional)</Label>
            <Input
              id="override-reason"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="e.g., Back-to-back jobs in same area..."
              className="mt-2"
              data-testid="override-reason-input"
            />
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowConflictDialog(false);
                setShowAssignDialog(true);
              }}
            >
              Choose Different Staff
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => handleAssign(true)}
              disabled={actionLoading}
              data-testid="override-conflict-btn"
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Assign Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Not Done Reason Dialog */}
      <Dialog open={showNotDoneDialog} onOpenChange={setShowNotDoneDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-amber-500" />
              Mark as Not Done
            </DialogTitle>
            <DialogDescription>
              {notDoneItem && (
                <span>Please provide a reason why "{notDoneItem.task}" could not be completed.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="not-done-reason">Reason</Label>
              <Textarea
                id="not-done-reason"
                value={notDoneReason}
                onChange={(e) => setNotDoneReason(e.target.value)}
                placeholder="e.g., Area inaccessible, Customer requested skip, Equipment not available..."
                rows={3}
                data-testid="not-done-reason-input"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700">
                <span className="font-medium">Note:</span> This item will be marked as Not Done with your reason recorded. You can still complete the job.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNotDoneDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleNotDoneSubmit}
              disabled={!notDoneReason.trim()}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="submit-not-done-btn"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Mark Not Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      <ImageLightbox
        open={lightboxOpen}
        index={lightboxIndex}
        slides={lightboxSlides}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

export default BookingDetailPage;
